/**
 * recruitApi — thin wrapper around the public `resolveRecruitToken` callable
 * (architecture-loop-a.md §7.8, WL-V1). Used only by the public `/empfangen`
 * landing page. No auth is required (the callable is public); the Firebase SDK
 * simply omits the bearer token when the user is not signed in.
 *
 * The callable is enumeration-safe: every negative case (missing / invalid /
 * expired / claimed / feature disabled) returns the uniform `{ valid: false }`.
 * This wrapper additionally fail-closes any transport error to `{ valid: false }`
 * so the landing page always has a well-typed result and simply falls back to its
 * generic variant.
 */

import { httpsCallable } from "firebase/functions";
import { functions } from "../firebaseConfig";
import { normalizeCallableError } from "./inboundApi";

/**
 * The sessionStorage key carrying the recruit token from the public landing page
 * (`/empfangen?ref={token}`, WL-V) through signup to the post-provisioning
 * auto-claim (WL-V3). CONTRACT: `EmpfangenPage` WRITES this key on load when a
 * `?ref` token is present; the onboarding auto-claim in `InboundActivation` READS
 * and CLEARS it via {@link takeRecruitRef} after the mailbox is provisioned. It is
 * the single source of truth for the key name — both sides import it here so it can
 * never drift.
 */
export const RECRUIT_REF_SESSION_KEY = "recruitRef";

/** Result of {@link resolveRecruitToken} (mirrors the callable output, §7.8). */
export interface RecruitResolveResult {
  valid: boolean;
  senderCompanyName?: string;
  invoiceNumberMasked?: string;
}

/**
 * Success result of {@link claimRecruitedInvoice} (mirrors the callable, §7.7 —
 * rev. 2026-07-04 DECOUPLE). The bind is synchronous; the `inbound_documents` doc
 * is created ASYNCHRONOUSLY by the `deliverClaimedInvoice` task, so there is no
 * `inboundDocumentId` here anymore — the invoice simply appears in the inbox once
 * delivered. No synchronous status tracking in v1.
 */
export interface RecruitClaimResult {
  claimAccepted: true;
  delivery: "queued";
}

/**
 * Resolves a recruit token to the pre-auth hook fields (sender company + masked
 * invoice number). Never rejects: on any error it resolves to `{ valid: false }`
 * so the caller can render the generic landing page.
 *
 * @param token the recruit token from `?ref=` on the landing page.
 * @returns the resolve result (always resolves).
 */
export async function resolveRecruitToken(
  token: string
): Promise<RecruitResolveResult> {
  try {
    const callable = httpsCallable<
      { token: string },
      RecruitResolveResult
    >(functions, "resolveRecruitToken");
    const res = await callable({ token });
    const data = res.data;
    // Defensive: coerce anything unexpected to the safe negative.
    if (!data || data.valid !== true) {
      return { valid: false };
    }
    return {
      valid: true,
      senderCompanyName: data.senderCompanyName,
      invoiceNumberMasked: data.invoiceNumberMasked,
    };
  } catch {
    // Transport / callable error → fall back to the generic landing page.
    return { valid: false };
  }
}

/**
 * Reads AND removes the recruit token stashed by the landing page (single-shot):
 * the token must never be claimed twice from the same session, so it is cleared
 * even if the subsequent claim call fails or the session is reloaded.
 *
 * @returns the token, or `null` when none is present / storage is unavailable.
 */
export function takeRecruitRef(): string | null {
  try {
    const token = sessionStorage.getItem(RECRUIT_REF_SESSION_KEY);
    if (token) sessionStorage.removeItem(RECRUIT_REF_SESSION_KEY);
    return token && token.length > 0 ? token : null;
  } catch {
    // Private-mode / storage-disabled → no auto-claim; harmless.
    return null;
  }
}

/**
 * Redeems a recruit token for the signed-in user (WL-V3 "Magic Moment", §7.7 —
 * rev. 2026-07-04 DECOUPLE). This ONLY performs the synchronous bind (Phase 1):
 * the sender's invoice is delivered ASYNCHRONOUSLY by a server-side task and will
 * appear in the inbox shortly after — there is no synchronous confirmation that
 * the document exists yet. Requires auth (the Firebase SDK attaches the bearer
 * token) AND a provisioned mailbox — so the onboarding must call this only AFTER
 * `provisionInboundAddress` has succeeded.
 *
 * Unlike {@link resolveRecruitToken} this THROWS on failure (normalized with a
 * `.code`), because the caller distinguishes the defined business errors
 * (`avv_required`, `token_invalid`, `token_expired`, `token_claimed`, `self_claim`,
 * `source_file_missing`, `feature_disabled`) to decide whether to show a hint — a
 * failed auto-claim never blocks the completed activation. `source_file_missing`
 * here means the doc-level PRE-CHECK failed (token left unclaimed, retryable) —
 * it is NOT the same as an async delivery failure (which the FE never observes
 * synchronously in v1).
 *
 * @param token the recruit token (from {@link takeRecruitRef}).
 * @returns `{ claimAccepted: true, delivery: 'queued' }` once the bind commits.
 * @throws Error (with `.code`) on any failure — the token stays unclaimed.
 */
export async function claimRecruitedInvoice(
  token: string
): Promise<RecruitClaimResult> {
  try {
    const callable = httpsCallable<{ token: string }, RecruitClaimResult>(
      functions,
      "claimRecruitedInvoice"
    );
    const res = await callable({ token });
    return res.data;
  } catch (err) {
    throw normalizeCallableError(err);
  }
}
